import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import moment from 'moment'
import {
  Text,
  Button,
  Box,
  useDialogControllers,
  TooltipRenderer,
  Skeleton
} from '@machobear/design-system'
import { Stack } from '@/components/structure'
import isVerified from '@/utils/is-verified'
import VerifiedBadge from '@/components/verified-badge'
import useGetTrustScoreCancelAvailability from '@/hooks/use-trust-score-cancel-availability'
import NoCreditHistoryModal from '@/components/no-credit-history-modal'
import { colors, fontWeights } from '@/styles'
import { Avatar, User, getColor, formatName } from './elements'
import CancelSubmitModal from './cancel-submit-modal'
import { BubbleTitle } from '../elements'

const RowWrapper = styled.div<{ isRenter?: boolean }>`
  display: flex;
  gap: 0 12px;
  align-items: center;
  ${props => props.isRenter && 'justify-content: space-between;'}
`

const ScoreTitle = styled.h3`
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  line-height: 20px;
  margin: 0;
`

const ScoreDescription = styled.p`
  font-size: 16px;
  color: ${colors.regular};
  line-height: 20px;
  letter-spacing: -0.2px;
  margin: 0;
`

const Name = styled.div`
  display: flex;
  color: ${colors.regular};
  font-weight: ${fontWeights.medium};
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.4px;
`

const BlogLink = styled.a`
  margin: 8px 0 0;
  color: ${colors.link};
  font-weight: ${fontWeights.medium};
  font-size: ${fontWeights.regular};
  line-height: 20px;
  letter-spacing: -0.4px;
`

const UpdatedText = styled.p<{ isRenter?: boolean }>`
  font-size: 12px;
  color: ${colors.mediumGrey};
  line-height: 24px;
  margin: ${props => (props.isRenter ? 8 : 0)}px 0 0;
  letter-spacing: -0.4px;
`

const isUnknown = (value: string) => !value || ['N/A', 'analyzing'].indexOf(value) > -1

const formatDate = (date: string) => moment(date, 'YYYY-MM-DD').format('MMM. DD, YYYY')

type TextHeaderProps = {
  user: User & { avatar?: { aws_s3_key?: string } } & {
    profile_progress?: {
      liv_score?: { missing?: { liv_verified_state?: boolean } }
    }
    check_credit_report_status?: { executed?: boolean }
    international_user?: string
  }
  userRole?: string
  title?: string
  score: string
  creditDescription?: string
  noDetails?: boolean
  rating: string
  updatedAt: string
}

const TextHeader: React.FC<TextHeaderProps> = ({
  user: profile,
  userRole,
  title,
  score,
  creditDescription,
  noDetails,
  rating,
  updatedAt
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { visible, showModal, closeModal } = useDialogControllers({
    cancelSubmit: false,
    noCreditHistory: false
  })
  const isRenter = userRole === 'tenant'
  const isSubmittedCard =
    rating === t('r.trust_score.completed.text', 'Completed') ||
    rating === t('r.trust_score.submitted.text', 'Submitted')
  const isRequestSubmitted = !profile?.profile_progress?.liv_score?.missing?.liv_verified_state
  const avatarUrl = profile?.avatar?.aws_s3_key
  const isCreditRequestSubmitted = profile?.check_credit_report_status?.executed
  const isInternationalUser = profile?.international_user === '1'
  const { data: cancelAvailability, isLoading: isLoadingCancelAvailability } =
    useGetTrustScoreCancelAvailability({ enabled: isRenter })
  const hasAbleToCancelApplication = cancelAvailability?.is_available_to_cancel

  const getBlogMessage = () => {
    if (score === 'excellent')
      return t('r.liv_score_card.view.improve_score_excellent.link', 'Learn more about Trust Score')
    if (score === 'not enough')
      return t('r.liv_score_card.view.improve_score_soft.link', 'See how you can build your score')
    return t('r.liv_score_card.view.improve_score.link', 'See how you can improve your score')
  }

  const cancelHandler = async () => {
    if (hasAbleToCancelApplication) {
      showModal('cancelSubmit')()
    } else {
      cancelCreditCheck()
    }
  }

  const cancelCreditCheck = async () => {
    closeModal('cancelSubmit')()
    showModal('noCreditHistory')()
  }

  const cancelButtonRenderer = () => {
    if (isLoadingCancelAvailability || profile?.check_credit_report_status === undefined) {
      return (
        <Box>
          <Skeleton.Button />
        </Box>
      )
    }
    if (isCreditRequestSubmitted) {
      return (
        <TooltipRenderer
          id="cancelToolTip"
          tooltip={
            <>
              <Box style={{ marginBottom: '12px' }}>
                <Text weight="strong">
                  {t('r.trust_score._request_cancel_tooltip_title', 'Credit check verified')}
                </Text>
              </Box>
              <Text size="small">
                {t(
                  'r.trust_score._request_cancel_tooltip_description',
                  'Your credit check information has been submitted to Equifax® and can no longer be cancelled.'
                )}
              </Text>
            </>
          }
        >
          {({ triggerProps }) => (
            <Box {...triggerProps}>
              <Button variant="ghost" type="button" disabled={true}>
                {t('b.profile.view.verified.text', 'Verified')}
              </Button>
            </Box>
          )}
        </TooltipRenderer>
      )
    }

    return (
      <Box>
        <Button variant="ghost" type="button" onClick={cancelHandler} disabled={false}>
          {t('commmon.cancel', 'Cancel')}
        </Button>
      </Box>
    )
  }

  return isRenter ? (
    <>
      <Stack gutter={16} width="100%" top={4}>
        <RowWrapper isRenter={isRenter}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ScoreTitle>{title}</ScoreTitle>
            <BubbleTitle
              label={t('r.liv_score_card.view.current_score.title', 'Trust Score')}
              color={
                isRequestSubmitted && isSubmittedCard
                  ? getColor('submitted')
                  : isRequestSubmitted
                  ? 'requested'
                  : isSubmittedCard
                  ? 'submitted'
                  : getColor(score)
              }
              top={0}
            >
              {rating}
            </BubbleTitle>
          </Box>
          {isRequestSubmitted && !isInternationalUser && cancelButtonRenderer()}
        </RowWrapper>
        <RowWrapper>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              maxWidth: isRequestSubmitted ? '425px' : 'none'
            }}
          >
            <ScoreDescription>{creditDescription}</ScoreDescription>
          </Box>
        </RowWrapper>
        {!isUnknown(score) && (
          <RowWrapper>
            <BlogLink
              id="application-trust-score-renter-read-more"
              href="https://liv.rent/blog/2018/10/improving-your-trust-score/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {!isRequestSubmitted && getBlogMessage()}
            </BlogLink>
          </RowWrapper>
        )}
        {!noDetails && (
          <RowWrapper>
            <UpdatedText isRenter={isRenter}>
              {t('b.liv_score_card.view.last_updated.text', 'Last Updated')}{' '}
              {`${formatDate(updatedAt)}`}
            </UpdatedText>
          </RowWrapper>
        )}
      </Stack>
      <CancelSubmitModal
        width={'xsmall'}
        visible={visible.cancelSubmit}
        onClose={closeModal('cancelSubmit')}
        cancelCreditCheck={cancelCreditCheck}
      />
      <NoCreditHistoryModal
        visible={visible.noCreditHistory}
        onClose={closeModal('noCreditHistory')}
        updateUser={() => dispatch.livScore.cancelCreditReportSubmission()}
      />
    </>
  ) : (
    <Stack gutter={12} width="100%" top={4}>
      <RowWrapper>
        <ScoreTitle>{title}</ScoreTitle>
        <BubbleTitle
          label={t('r.liv_score_card.view.current_score.title', 'Trust Score')}
          color={isSubmittedCard ? 'submitted' : getColor(score)}
          top={0}
        >
          {rating}
        </BubbleTitle>
      </RowWrapper>
      <RowWrapper>
        <Avatar avatarUrl={avatarUrl} />
        <Name>
          {formatName(profile)}
          <VerifiedBadge
            type="id"
            width={20}
            height={20}
            left={6}
            bottom={0}
            verified={isVerified(profile)}
          />
        </Name>
      </RowWrapper>
      <RowWrapper>
        <ScoreDescription>{creditDescription}</ScoreDescription>
      </RowWrapper>
      {isRenter && !isUnknown(score) && (
        <RowWrapper>
          <BlogLink
            id="application-trust-score-renter-read-more"
            href="https://liv.rent/blog/2018/10/improving-your-trust-score/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getBlogMessage()}
          </BlogLink>
        </RowWrapper>
      )}
      {!noDetails && (
        <RowWrapper>
          <UpdatedText isRenter={isRenter}>
            {t('b.liv_score_card.view.last_updated.text', 'Last Updated:')}{' '}
            {`${formatDate(updatedAt)}`}
          </UpdatedText>
        </RowWrapper>
      )}
    </Stack>
  )
}

export default TextHeader
